import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, tokens } from "./ThemeContext";
import TipComplianceChecker from "./TipComplianceChecker";
import NewHireChecklist from "./NewHireChecklist";
import ExemptClassifier from "./ExemptClassifier";
import ExemptGuide from "./ExemptGuide";
import BreakRestLookup from "./BreakRestLookup";

// ─── Tool Registry ────────────────────────────────────────────────────────────
const TOOLS = [
  {
    id: "tip",
    label: "Tip Wage Checker",
    shortLabel: "Tip Wages",
    icon: "💵",
    tag: "All 50 States",
    tagColor: "#F59E0B",
    desc: "Verify cash wage floors, tip credit eligibility, the 80/20 rule, and tip pool composition. Includes 2026 locality overrides.",
    component: TipComplianceChecker,
  },
  {
    id: "newhire",
    label: "New Hire Checklist",
    shortLabel: "New Hire",
    icon: "📋",
    tag: "Federal + State",
    tagColor: "#818CF8",
    desc: "State-specific onboarding checklist: required forms, posters, notices, and I-9/W-4 deadlines for every state.",
    component: NewHireChecklist,
  },
  {
    id: "exempt",
    label: "Exempt/Non-Exempt Classifier",
    shortLabel: "Exemptions",
    icon: "⚖️",
    tag: "FLSA Analysis",
    tagColor: "#10B981",
    desc: "Walk through the FLSA salary basis, salary level, and duties tests to determine whether a role qualifies for white-collar exemptions.",
    component: ExemptClassifier,
  },
  {
    id: "exemptguide",
    label: "Exemption Reference Guide",
    shortLabel: "Exempt Guide",
    icon: "📖",
    tag: "FLSA Reference",
    tagColor: "#06B6D4",
    desc: "Plain-language breakdown of every major FLSA exemption: salary thresholds, duties tests, common pitfalls, and employer action items.",
    component: ExemptGuide,
  },
  {
    id: "breaks",
    label: "Break & Rest Rules Lookup",
    shortLabel: "Breaks",
    icon: "⏱️",
    tag: "All 50 States",
    tagColor: "#F97316",
    desc: "Look up mandatory meal and rest break requirements by state, industry, and employee type — including minor-specific rules.",
    component: BreakRestLookup,
  },
];

// ─── Theme toggle button ──────────────────────────────────────────────────────
function ThemeToggle({ t }) {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 8, cursor: "pointer",
        background: t.toggleBg, border: `1px solid ${t.navBorder}`,
        color: t.toggleColor, fontSize: 15, flexShrink: 0,
        transition: "background 0.2s",
        marginLeft: 8,
      }}
    >
      {theme === "dark" ? "☀️" : "🌙"}
    </button>
  );
}

// ─── Tool Card ────────────────────────────────────────────────────────────────
function ToolCard({ tool, onSelect, t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => onSelect(tool.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? t.cardBgHover : t.cardBg,
        border: `1px solid ${hovered ? t.cardBorderHover : t.cardBorder}`,
        borderRadius: 14, padding: 24, cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s, transform 0.15s",
        transform: hovered ? "translateY(-2px)" : "none",
        display: "flex", flexDirection: "column", gap: 14,
        boxShadow: hovered ? "0 4px 16px rgba(0,0,0,0.06)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{
          width: 44, height: 44,
          background: t.cardBorder,
          borderRadius: 10, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 20, flexShrink: 0,
        }}>
          {tool.icon}
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: tool.tagColor,
          fontFamily: "'IBM Plex Mono', monospace",
          background: `${tool.tagColor}14`,
          border: `1px solid ${tool.tagColor}30`,
          borderRadius: 20, padding: "3px 10px",
          whiteSpace: "nowrap", alignSelf: "flex-start",
        }}>
          {tool.tag}
        </span>
      </div>
      <p style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, lineHeight: 1.3, margin: 0 }}>
        {tool.label}
      </p>
      <p style={{ fontSize: 12, color: t.textSecondary, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.7, margin: 0, flex: 1 }}>
        {tool.desc}
      </p>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: tool.tagColor, fontFamily: "'IBM Plex Mono', monospace", marginTop: 4 }}>
        Open tool →
      </div>
    </div>
  );
}

// ─── Home Dashboard ───────────────────────────────────────────────────────────
function Home({ onSelect, t, navigate }) {
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 80px" }}>
      {/* Hero */}
      <div style={{ marginBottom: 48 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: t.accentBg, border: `1px solid ${t.accentBorder}`,
          borderRadius: 20, padding: "5px 14px", marginBottom: 20,
        }}>
          <div style={{ width: 6, height: 6, background: t.accent, borderRadius: "50%" }} />
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: t.accent, fontFamily: "'IBM Plex Mono', monospace" }}>
            Free · 2026 Data · No Login Required
          </span>
        </div>
        <h1 style={{ fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 800, color: t.textPrimary, margin: "0 0 16px", lineHeight: 1.15 }}>
          HR Compliance Tools<br />
          <span style={{ color: t.accent }}>Built for Employers</span>
        </h1>
        <p style={{ fontSize: 15, color: t.textSecondary, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.7, maxWidth: 560, margin: "0 0 24px" }}>
          Free, research-grade compliance tools covering wage law, onboarding,
          FLSA exemptions, and break rules — updated to 2026 federal and state standards.
        </p>
        {/* Blog CTA */}
        <button
          onClick={() => navigate("/blog")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 18px", borderRadius: 8, cursor: "pointer",
            background: t.accentBg, border: `1px solid ${t.accentBorder}`,
            color: t.accent, fontSize: 13, fontWeight: 600,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          📰 Read the compliance blog →
        </button>
      </div>

      {/* Tool grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {TOOLS.map((tool) => (
          <ToolCard key={tool.id} tool={tool} onSelect={onSelect} t={t} />
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${t.navBorder}`, marginTop: 64, paddingTop: 32 }}>
        <p style={{ fontSize: 11, color: t.textMuted, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.7, margin: 0 }}>
          WorkplacePrompts.ai provides free HR compliance research tools for informational purposes only.
          Nothing on this site constitutes legal advice. Laws change frequently — always verify current
          requirements with a licensed employment attorney or your state labor department before taking
          action. Payroll platform links on this site may be affiliate links; we may earn a commission
          if you sign up through them.
        </p>
      </div>
    </div>
  );
}

// ─── App Shell ────────────────────────────────────────────────────────────────
export default function ToolShell() {
  const [activeTool, setActiveTool] = useState(null);
  const { theme } = useTheme();
  const t = tokens[theme];
  const navigate = useNavigate();

  const ActiveComponent = activeTool
    ? TOOLS.find((tool) => tool.id === activeTool)?.component
    : null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: ${t.appBg}; transition: background 0.2s; }
        .wp-tab:hover { color: ${theme === "dark" ? "#CBD5E1" : "#0F172A"} !important; }
        .wp-tabs::-webkit-scrollbar { display: none; }
      `}</style>

      <div style={{ minHeight: "100vh", background: t.appBg, fontFamily: "'Syne', sans-serif", transition: "background 0.2s" }}>

        {/* ── Nav ── */}
        <nav style={{ background: t.navBg, borderBottom: `1px solid ${t.navBorder}`, position: "sticky", top: 0, zIndex: 100, transition: "background 0.2s" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", gap: 0, height: 56 }}>

            {/* Logo */}
            <div
              onClick={() => setActiveTool(null)}
              style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 24, cursor: "pointer", flexShrink: 0 }}
            >
              <div style={{
                width: 28, height: 28,
                background: "linear-gradient(135deg, #F59E0B, #D97706)",
                borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0,
              }}>⚖️</div>
              <span style={{ fontSize: 14, fontWeight: 800, color: t.textPrimary, letterSpacing: "0.02em", lineHeight: 1, whiteSpace: "nowrap" }}>
                WorkplacePrompts<span style={{ color: t.accent }}>.ai</span>
              </span>
            </div>

            {/* Tool tabs */}
            <div className="wp-tabs" style={{ display: "flex", alignItems: "stretch", gap: 2, flex: 1, overflowX: "auto", msOverflowStyle: "none", scrollbarWidth: "none", height: "100%" }}>
              {TOOLS.map((tool) => (
                <button
                  key={tool.id}
                  className="wp-tab"
                  onClick={() => setActiveTool(tool.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "0 14px", height: "100%",
                    fontSize: 12,
                    fontWeight: activeTool === tool.id ? 700 : 500,
                    fontFamily: "'IBM Plex Mono', monospace",
                    color: activeTool === tool.id ? t.textPrimary : t.textSecondary,
                    cursor: "pointer", whiteSpace: "nowrap",
                    background: "none", border: "none",
                    borderBottom: activeTool === tool.id ? `2px solid ${t.accent}` : "2px solid transparent",
                    transition: "color 0.15s, border-color 0.15s",
                  }}
                >
                  <span style={{ fontSize: 14 }}>{tool.icon}</span>
                  {tool.shortLabel}
                </button>
              ))}
            </div>

            {/* Right side: Blog link + back button + theme toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto", flexShrink: 0 }}>
              {activeTool && (
                <button
                  onClick={() => setActiveTool(null)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "0 12px",
                    height: 32, fontSize: 12, fontWeight: 500,
                    fontFamily: "'IBM Plex Mono', monospace",
                    color: t.textSecondary, background: "none", border: "none",
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  ← All Tools
                </button>
              )}
              <button
                onClick={() => navigate("/blog")}
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "0 12px",
                  height: 32, fontSize: 12, fontWeight: 500,
                  fontFamily: "'IBM Plex Mono', monospace",
                  color: t.textSecondary, background: "none", border: "none",
                  cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                Blog
              </button>
              <ThemeToggle t={t} />
            </div>
          </div>
        </nav>

        {/* ── Content ── */}
        {ActiveComponent ? (
          <ActiveComponent />
        ) : (
          <Home onSelect={setActiveTool} t={t} navigate={navigate} />
        )}
      </div>
    </>
  );
}
